import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitQuery } from '@y42/git';
import { BehaviorSubject, finalize, map, NEVER, Observable, of, race, switchMap, take, tap, timer } from 'rxjs';
import { FinalJobStatuses, JobQuery, JobStoreModel } from '../../state';
import { JobTriggerService } from './job-trigger.service';

export const JOB_CANCELATION_TIMEOUT = 1000 * 60;

@Injectable()
export abstract class AbstractJobCancelationService {
  abstract cancelJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']): Observable<void>;
  abstract selectIsJobCanceling(id: JobStoreModel['request_id']): Observable<boolean>;
}

@Injectable()
export class JobCancelationService implements AbstractJobCancelationService {
  constructor(
    private readonly jobQuery: JobQuery,
    private readonly gitQuery: GitQuery,
    private readonly triggerService: JobTriggerService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly cancelingJobs$$ = new BehaviorSubject<Set<string>>(new Set());

  cancelJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']): Observable<void> {
    const job = this.jobQuery.getEntity(id);
    if (!job) {
      throw new Error(`Job '${id}' was not found`);
    }

    const activeBranchName = this.gitQuery.getActiveBranchName();
    const jobTriggeredFromBranch = job.request.storage.branch;
    if (activeBranchName !== jobTriggeredFromBranch) {
      throw new Error(
        `Job was triggered from branch '${jobTriggeredFromBranch}' and can't be cancelled from current branch '${activeBranchName}'`,
      );
    }
    return this.cancelingJobs$$.value.has(id) ? of(undefined) : this.requestJobCancellation(id, path);
  }

  selectIsJobCanceling(id: JobStoreModel['request_id']): Observable<boolean> {
    return this.cancelingJobs$$.pipe(map((cancelingJobs) => cancelingJobs.has(id)));
  }

  private requestJobCancellation(id: JobStoreModel['request_id'], path: JobStoreModel['path']): Observable<void> {
    this.cancelingJobs$$.next(new Set(this.cancelingJobs$$.value).add(id));
    return this.triggerService.cancelJob(id, path).pipe(
      tap((response) => {
        if (response && !response.success && response.error) {
          this.errorHandlerService.error(`[JobCancelationService.requestJobCancellation]: job cancellation failed.`, {
            context: {
              jobId: id,
              error: response.error,
            },
          });
          throw new Error(response.error);
        }
      }),
      switchMap(() => this.observeJobStatusChange(id)),
      map(() => undefined),
      take(1),
      finalize(() => {
        const newSet = new Set(this.cancelingJobs$$.value);
        newSet.delete(id);
        this.cancelingJobs$$.next(newSet);
      }),
    );
  }

  private observeJobStatusChange(requestId: string) {
    return race(
      this.jobQuery
        .selectEntity(requestId)
        .pipe(switchMap((job) => (job && FinalJobStatuses.includes(job.status) ? of(job) : NEVER))),
      timer(JOB_CANCELATION_TIMEOUT),
    );
  }
}
